import { AdvancedSearch, SearchValueType } from '../../../ui/AdvancedSearch'
import { QUERY as BOARD_DEVICE_ASSOCIATIONS, useBoardDeviceAssociations } from '../hooks/useBoardDeviceAssociations'
import { BoardDeviceAssociationSortBy, BoardDeviceAssociationSortOrder } from '../types'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Link,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography
} from '@material-ui/core'
import React, { useEffect, useMemo, useState } from 'react'

import { BatchUnPair } from '../components/BatchUnpair'
import { Add, Check, Close, Search } from '@material-ui/icons'
import { CsvExport } from '../../../ui/CsvExport'
import { CsvImport } from '../components/CsvImport'
import { FULL_ACCESS } from '../../../constants'
import { FlexHorizontal } from '@vestaboard/installables'
import { FormattedDateTime } from '../../formatted-date-time'
import { ListBoardDeviceAssociations_listBoardDeviceAssociations_boardDeviceAssociations } from '../hooks/__generated__/ListBoardDeviceAssociations'
import { SearchBoiSerialComponent } from '../components/SearchBoiSerial'
import { SearchDeviceId } from '../components/SearchDeviceId'
import { SearchPairingCodeComponent } from '../components/SearchPairingCode'
import { SearchSSIDComponent } from '../components/SearchSSID'
import { SuperGrid } from '../../../ui/SuperGrid'
import { TruncatedId } from '../../truncated-id'
import { gql } from 'apollo-boost'
import { BoardDeviceAssociationInput } from '../../../../gen/__generated__/globalTypes'
import { isUUID, isBoiSerialNumber } from '../../../utils'
import moment from 'moment'
import { useGetViewer } from '../../../api/useGetViewer'
import { useListDisplayGroups } from '../../note-array/hooks/useListDisplayGroups'
import { useAuth0 } from '@auth0/auth0-react'
import { useMutation } from '@apollo/react-hooks'
import { useRequestFirmwareUpgrade } from '../../device-detail/DeviceInformation'
import { sleep } from '../../../utils/sleep'

const DEFAULT_OS_VERSION_ID = '4fd8cd13-d3cb-43c0-ac82-e0bd2a2c5f39'

const meetsWebsocketMinimum = (version: string | null | undefined): boolean => {
  if (!version) return false
  if (version.includes('developer') || version.includes('rc')) return true
  const match = version.match(/v?(\d+)\.(\d+)\.(\d+)/)
  if (!match) return false
  const [, major, minor, patch] = match.map(Number)
  return major > 3 || (major === 3 && minor > 1) || (major === 3 && minor === 1 && patch >= 5)
}

const REINDEX_MUTATION = gql`
  mutation indexBoardDeviceAssociationByBoard($boardId: String!) {
    indexBoardDeviceAssociationByBoard(boardId: $boardId)
  }
`

const DATE_TIME_FORMAT = `YYYY-MM-DD hh:mm:ss`

const exportDateFieldLabels: Record<string, string> = {
  boardDeviceAssociationCreated: 'Pairing Date',
  plusRenewalDate: 'Renewal Date',
  productApplicationCreated: 'Subscription Created',
  stripeCurrentPeriodEnd: 'Stripe Period End',
  stripeCurrentPeriodStart: 'Stripe Period Start',
  stripeSubscriptionCreated: 'Stripe Sub Created',
  stripeStartDate: 'Stripe Start Date',
  stripeCanceledAt: 'Stripe Canceled At',
  stripeCancelAt: 'Stripe Cancel At',
  stripeEndedAt: 'Stripe Ended At',
  stripeTrialStart: 'Stripe Trial Start',
  stripeTrialEnd: 'Stripe Trial End',
  stripeBillingCycleAnchor: 'Stripe Billing Anchor'
}

type BoardDeviceAssociation = ListBoardDeviceAssociations_listBoardDeviceAssociations_boardDeviceAssociations

interface BoardToGroupMap {
  [boardId: string]: {
    groupId: string
    groupTitle: string
  }
}

const columns = (boardToGroupMap: BoardToGroupMap, onReindex: (boardId: string) => void) => [
  {
    title: 'Board ID',
    key: BoardDeviceAssociationSortBy.boardId,
    width: 150,
    render: (bda: BoardDeviceAssociation) => <TruncatedId value={bda.boardId} path={`/boards/${bda.boardId}`} />
  },
  {
    title: 'Board Title',
    key: BoardDeviceAssociationSortBy.boardTitle,
    width: 150,
    render: (bda: BoardDeviceAssociation) => bda.boardTitle || 'Untitled'
  },
  {
    title: 'Device ID',
    key: BoardDeviceAssociationSortBy.deviceId,
    width: 150,
    render: (bda: BoardDeviceAssociation) => <TruncatedId value={bda.deviceId} path={`/devices/${bda.deviceId}`} />
  },
  {
    title: 'Device Created',
    width: 120,
    key: BoardDeviceAssociationSortBy.deviceCreated,
    render: (bda: BoardDeviceAssociation) => <FormattedDateTime format='MM/DD/YYYY' value={bda.deviceCreated} />
  },
  {
    title: 'Board Created',
    width: 120,
    key: BoardDeviceAssociationSortBy.boardCreated,
    render: (bda: BoardDeviceAssociation) => <FormattedDateTime format={'MM/DD/YYYY'} value={bda.boardCreated} />
  },
  {
    title: 'Pairing Date',
    width: 120,
    key: BoardDeviceAssociationSortBy.boardDeviceAssociationCreated,
    render: (bda: BoardDeviceAssociation) => (
      <FormattedDateTime format='MM/DD/YYYY' value={bda.boardDeviceAssociationCreated} />
    )
  },
  {
    title: 'Device Last Seen',
    width: 120,
    key: BoardDeviceAssociationSortBy.deviceLastSeen,
    render: (bda: BoardDeviceAssociation) => <FormattedDateTime format='MM/DD/YYYY' value={bda.deviceLastSeen} />
  },
  {
    title: 'Board Deleted',
    width: 120,
    key: BoardDeviceAssociationSortBy.boardDeleted,
    render: (bda: BoardDeviceAssociation) => (bda.boardDeleted ? <Check /> : '')
  },
  {
    title: 'Pairing Code',
    width: 120,
    key: BoardDeviceAssociationSortBy.pairingCode,
    render: (bda: BoardDeviceAssociation) => bda.pairingCode || ''
  },
  {
    title: 'SSID',
    key: BoardDeviceAssociationSortBy.ssid,
    width: 170,
    render: (bda: BoardDeviceAssociation) => bda.ssid || ''
  },
  {
    title: 'Firmware Version',
    width: 130,
    key: BoardDeviceAssociationSortBy.firmwareVersion,
    render: (bda: BoardDeviceAssociation) => bda.firmwareVersion || ''
  },
  {
    title: 'OS Version Name',
    width: 130,
    key: BoardDeviceAssociationSortBy.osVersionName,
    render: (bda: BoardDeviceAssociation) => bda.osVersionName || ''
  },
  {
    title: 'Owner ID',
    key: BoardDeviceAssociationSortBy.personId,
    width: 150,
    render: (bda: BoardDeviceAssociation) =>
      bda.personId ? <TruncatedId value={bda.personId} path={`/person/${bda.personId}`} /> : ''
  },
  {
    title: 'First Name',
    width: 150,
    key: BoardDeviceAssociationSortBy.personFirstName,
    render: (bda: BoardDeviceAssociation) => bda.personFirstName || ''
  },
  {
    title: 'Last Name',
    width: 150,
    key: BoardDeviceAssociationSortBy.personLastName,
    render: (bda: BoardDeviceAssociation) => bda.personLastName || ''
  },
  {
    title: 'Owner Email',
    width: 200,
    key: BoardDeviceAssociationSortBy.accountEmailAddress,
    render: (bda: BoardDeviceAssociation) => bda.accountEmailAddress || ''
  },
  {
    title: 'Members',
    width: 100,
    key: BoardDeviceAssociationSortBy.membersCount,
    render: (bda: BoardDeviceAssociation) => bda.memberIds.length || '0'
  },
  {
    title: 'Online',
    width: 100,
    key: BoardDeviceAssociationSortBy.online,
    render: (bda: BoardDeviceAssociation) => (bda.online ? <Check /> : '')
  },
  {
    title: 'Last Message',
    width: 120,
    key: BoardDeviceAssociationSortBy.lastMessage,
    render: (bda: BoardDeviceAssociation) =>
      bda.lastMessage ? <FormattedDateTime format='MM/DD/YYYY' value={bda.lastMessage} /> : ''
  },
  {
    title: 'Messages Sent',
    width: 150,
    key: BoardDeviceAssociationSortBy.messagesAllTime,
    render: (bda: BoardDeviceAssociation) => (bda.messagesAllTime || 0).toLocaleString()
  },
  {
    title: 'Messages (30 Days)',
    width: 150,
    key: BoardDeviceAssociationSortBy.messagesInPastThirtyDays,
    render: (bda: BoardDeviceAssociation) => (bda.messagesInPastThirtyDays || 0).toLocaleString()
  },
  {
    title: 'Messages (7 Days)',
    width: 150,
    key: BoardDeviceAssociationSortBy.messagesInPastSevenDays,
    render: (bda: BoardDeviceAssociation) => (bda.messagesInPastSevenDays || 0).toLocaleString()
  },
  {
    title: 'Messages (1 Day)',
    width: 150,
    key: BoardDeviceAssociationSortBy.messagesInPastDay,
    render: (bda: BoardDeviceAssociation) => (bda.messagesInPastDay || 0).toLocaleString()
  },
  {
    title: 'Channels',
    width: 150,
    key: BoardDeviceAssociationSortBy.channels,
    render: (bda: BoardDeviceAssociation) => (bda.channels || 0).toLocaleString()
  },
  {
    title: 'Enabled Channels',
    width: 150,
    key: BoardDeviceAssociationSortBy.enabledChannels,
    render: (bda: BoardDeviceAssociation) => (bda.enabledChannels || 0).toLocaleString()
  },
  {
    title: 'Plus Subscription ID',
    width: 150,
    key: BoardDeviceAssociationSortBy.productApplicationId,
    render: (bda: BoardDeviceAssociation) =>
      bda.productApplicationId ? (
        <TruncatedId value={bda.productApplicationId} path={`/plus/${bda.productApplicationId}`} />
      ) : (
        ''
      )
  },
  {
    title: 'Plus Subscription Created',
    width: 200,
    key: BoardDeviceAssociationSortBy.productApplicationCreated,
    render: (bda: BoardDeviceAssociation) =>
      bda.productApplicationCreated ? (
        <FormattedDateTime format={DATE_TIME_FORMAT} value={bda.productApplicationCreated} />
      ) : (
        ''
      )
  },
  {
    title: 'Plus Renewal Date',
    width: 200,
    key: BoardDeviceAssociationSortBy.plusRenewalDate,
    render: (bda: BoardDeviceAssociation) =>
      bda.plusRenewalDate ? <FormattedDateTime format='MM/DD/YYYY' value={bda.plusRenewalDate} /> : ''
  },
  {
    title: 'Subscription Frequency',
    width: 200,
    key: BoardDeviceAssociationSortBy.productPricingRecurringFrequency,
    render: (bda: BoardDeviceAssociation) => bda.productPricingRecurringFrequency || ''
  },
  {
    title: 'Yocto Status',
    width: 150,
    key: BoardDeviceAssociationSortBy.yoctoStatus,
    render: (bda: BoardDeviceAssociation) => bda.yoctoStatus || ''
  },
  {
    title: 'Device Model',
    width: 150,
    key: BoardDeviceAssociationSortBy.deviceModelTitle,
    render: (bda: BoardDeviceAssociation) => bda.deviceModelTitle || ''
  },
  {
    title: 'BOI Serial Number',
    width: 170,
    render: (bda: BoardDeviceAssociation) => bda.boiSerialNumber || ''
  },
  {
    title: 'Stripe Sub ID',
    width: 170,
    key: BoardDeviceAssociationSortBy.stripeSubscriptionId,
    render: (bda: BoardDeviceAssociation) => bda.stripeSubscriptionId || ''
  },
  {
    title: 'Stripe Status',
    width: 120,
    key: BoardDeviceAssociationSortBy.stripeStatus,
    render: (bda: BoardDeviceAssociation) => bda.stripeStatus || ''
  },
  {
    title: 'Stripe Period End',
    width: 150,
    key: BoardDeviceAssociationSortBy.stripeCurrentPeriodEnd,
    render: (bda: BoardDeviceAssociation) =>
      bda.stripeCurrentPeriodEnd ? <FormattedDateTime format='MM/DD/YYYY' value={bda.stripeCurrentPeriodEnd} /> : ''
  },
  {
    title: 'Stripe Interval',
    width: 120,
    key: BoardDeviceAssociationSortBy.stripeInterval,
    render: (bda: BoardDeviceAssociation) => bda.stripeInterval || ''
  },
  {
    title: 'Stripe Period Start',
    width: 150,
    key: BoardDeviceAssociationSortBy.stripeCurrentPeriodStart,
    render: (bda: BoardDeviceAssociation) =>
      bda.stripeCurrentPeriodStart ? <FormattedDateTime format='MM/DD/YYYY' value={bda.stripeCurrentPeriodStart} /> : ''
  },
  {
    title: 'Stripe Sub Created',
    width: 150,
    key: BoardDeviceAssociationSortBy.stripeSubscriptionCreated,
    render: (bda: BoardDeviceAssociation) =>
      bda.stripeSubscriptionCreated ? <FormattedDateTime format='MM/DD/YYYY' value={bda.stripeSubscriptionCreated} /> : ''
  },
  {
    title: 'Stripe Start Date',
    width: 150,
    key: BoardDeviceAssociationSortBy.stripeStartDate,
    render: (bda: BoardDeviceAssociation) =>
      bda.stripeStartDate ? <FormattedDateTime format='MM/DD/YYYY' value={bda.stripeStartDate} /> : ''
  },
  {
    title: 'Stripe Canceled At',
    width: 150,
    key: BoardDeviceAssociationSortBy.stripeCanceledAt,
    render: (bda: BoardDeviceAssociation) =>
      bda.stripeCanceledAt ? <FormattedDateTime format='MM/DD/YYYY' value={bda.stripeCanceledAt} /> : ''
  },
  {
    title: 'Stripe Cancel At',
    width: 150,
    key: BoardDeviceAssociationSortBy.stripeCancelAt,
    render: (bda: BoardDeviceAssociation) =>
      bda.stripeCancelAt ? <FormattedDateTime format='MM/DD/YYYY' value={bda.stripeCancelAt} /> : ''
  },
  {
    title: 'Stripe Ended At',
    width: 150,
    key: BoardDeviceAssociationSortBy.stripeEndedAt,
    render: (bda: BoardDeviceAssociation) =>
      bda.stripeEndedAt ? <FormattedDateTime format='MM/DD/YYYY' value={bda.stripeEndedAt} /> : ''
  },
  {
    title: 'Stripe Trial Start',
    width: 150,
    key: BoardDeviceAssociationSortBy.stripeTrialStart,
    render: (bda: BoardDeviceAssociation) =>
      bda.stripeTrialStart ? <FormattedDateTime format='MM/DD/YYYY' value={bda.stripeTrialStart} /> : ''
  },
  {
    title: 'Stripe Trial End',
    width: 150,
    key: BoardDeviceAssociationSortBy.stripeTrialEnd,
    render: (bda: BoardDeviceAssociation) =>
      bda.stripeTrialEnd ? <FormattedDateTime format='MM/DD/YYYY' value={bda.stripeTrialEnd} /> : ''
  },
  {
    title: 'Stripe Billing Anchor',
    width: 150,
    key: BoardDeviceAssociationSortBy.stripeBillingCycleAnchor,
    render: (bda: BoardDeviceAssociation) =>
      bda.stripeBillingCycleAnchor ? <FormattedDateTime format='MM/DD/YYYY' value={bda.stripeBillingCycleAnchor} /> : ''
  },
  {
    title: 'Hubspot Deal',
    width: 150,
    key: BoardDeviceAssociationSortBy.hubspotDealId,
    render: (bda: BoardDeviceAssociation) =>
      bda.hubspotDealId ? (
        <Link
          href={`https://app.hubspot.com/contacts/1984131/deal/${bda.hubspotDealId}`}
          target='_blank'
          rel='noopener noreferrer'>
          {bda.hubspotDealId}
        </Link>
      ) : (
        ''
      )
  },
  {
    title: 'Transition',
    width: 130,
    key: BoardDeviceAssociationSortBy.transitionStrategy,
    render: (bda: BoardDeviceAssociation) => `${bda.transitionStrategy || 'classic'}`
  },
  {
    title: 'Internal',
    width: 100,
    key: BoardDeviceAssociationSortBy.internalBoard,
    render: (bda: BoardDeviceAssociation) => (bda.internalBoard ? <Check /> : '')
  },
  {
    title: 'Note Array',
    width: 200,
    render: (bda: BoardDeviceAssociation) => {
      const groupInfo = boardToGroupMap[bda.boardId]
      if (!groupInfo) return ''
      return (
        <Chip
          size='small'
          label={groupInfo.groupTitle || 'Untitled'}
          clickable
          component='a'
          href={`/note-arrays/${groupInfo.groupId}`}
          color='primary'
          variant='outlined'
        />
      )
    }
  },
  {
    title: 'Last Indexed',
    width: 220,
    key: BoardDeviceAssociationSortBy.lastIndexed,
    render: (bda: BoardDeviceAssociation) => (
      <FlexHorizontal>
        <Box style={{ paddingTop: 7 }}>
          {bda.lastIndexed ? <FormattedDateTime format='MM/DD/YYYY hh:mm a' value={bda.lastIndexed} /> : ''}{' '}
        </Box>
      </FlexHorizontal>
    )
  },
  {
    title: 'Reindex',
    width: 100,
    render: (bda: BoardDeviceAssociation) => (
      <Button size='small' variant='outlined' onClick={() => onReindex(bda.boardId)}>
        Reindex
      </Button>
    )
  }
]

type FilterKey = Exclude<keyof BoardDeviceAssociationInput, 'sortBy' | 'sortOrder' | 'page' | 'perPage'>

const toKeyValue = (key: string) => ({ key, value: key })

interface SearchTypeEntry {
  key: FilterKey
  title: string
  type: 'text' | 'select' | 'range' | 'boolean' | 'dateRange' | 'button'
  options?: Array<{ key: string; value: string }>
}

const entry = (e: SearchTypeEntry): SearchTypeEntry => e

const searchTypes: SearchTypeEntry[] = [
  entry({ key: 'terms', title: 'All', type: 'text' }),
  entry({
    key: 'yoctoStatus',
    title: 'Yocto Status',
    type: 'select',
    options: ['YoctoInstalled', 'Rebooting', 'EligibleForYoctoAsOf', 'UpgraderInstallationBegan', 'UpgraderInstallationCompleted', 'Unknown'].map(toKeyValue)
  }),
  entry({
    key: 'deviceModelTitle',
    title: 'Device Model',
    type: 'select',
    options: ['Flagship EVT', 'Flagship DVT', 'Flagship PVT', 'Flagship White EVT', 'Flagship White DVT', 'Flagship White PVT'].map(toKeyValue)
  }),
  entry({ key: 'boardDeleted', title: 'Board Deleted', type: 'boolean' }),
  entry({ key: 'online', title: 'Online', type: 'boolean' }),
  entry({
    key: 'productPricingRecurringFrequency',
    title: 'Subscription Frequency',
    type: 'select',
    options: ['Annually', 'EveryThreeYears'].map(toKeyValue)
  }),
  entry({ key: 'messagesAllTime', title: 'Messages Sent All Time', type: 'range' }),
  entry({ key: 'messagesInPastDay', title: 'Messages In Past Day', type: 'range' }),
  entry({ key: 'messagesInPastSevenDays', title: 'Messages In Past Seven Days', type: 'range' }),
  entry({ key: 'messagesInPastThirtyDays', title: 'Messages In Past Thirty Days', type: 'range' }),
  entry({ key: 'boardCreated', title: 'Board Created Date', type: 'dateRange' }),
  entry({ key: 'boardDeviceAssociationCreated', title: 'Board Paired Date', type: 'dateRange' }),
  entry({ key: 'deviceCreated', title: 'Device Created Date', type: 'dateRange' }),
  entry({ key: 'deviceLastSeen', title: 'Device Last Seen', type: 'dateRange' }),
  entry({ key: 'lastMessage', title: 'Last Message', type: 'dateRange' }),
  entry({ key: 'productApplicationCreated', title: 'Product Application Created', type: 'dateRange' }),
  entry({ key: 'plusRenewalDate', title: 'Plus Renewal Date', type: 'dateRange' }),
  entry({ key: 'firmwareVersion', title: 'Firmware Version (Unknown, PVT, YOCTO1-3, vX.X.X)', type: 'text' }),
  entry({ key: 'osVersionName', title: 'OS Version Name', type: 'text' }),
  entry({ key: 'mysteryBoards', title: 'Mystery Boards', type: 'button' }),
  entry({ key: 'isPlus', title: 'Plus Boards', type: 'button' }),
  entry({ key: 'hasStripeSubscription', title: 'Stripe Plus Boards', type: 'button' }),
  entry({ key: 'stripeCurrentPeriodEnd', title: 'Stripe Period End', type: 'dateRange' }),
].sort((a, b) => a.title.localeCompare(b.title))

interface SecondaryFilter {
  id: number
  searchType: FilterKey
  searchValue: SearchValueType
}

const filterableSearchTypes = searchTypes.filter(t => t.type !== 'button')

const SecondaryFilterRow = ({
  filter,
  usedKeys,
  onChange,
  onRemove
}: {
  filter: SecondaryFilter
  usedKeys: Set<string>
  onChange: (updated: SecondaryFilter) => void
  onRemove: () => void
}) => {
  const selectedType = searchTypes.find(t => t.key === filter.searchType)
  const isRange = filter.searchValue !== null && typeof filter.searchValue === 'object'
  const [rangeMin, setRangeMin] = useState(() => (isRange && selectedType?.type === 'range' ? (filter.searchValue as { min: number; max: number }).min : 0))
  const [rangeMax, setRangeMax] = useState(() => (isRange && selectedType?.type === 'range' ? (filter.searchValue as { min: number; max: number }).max : 0))
  const [dateRangeMin, setDateRangeMin] = useState(() =>
    isRange && selectedType?.type === 'dateRange' && (filter.searchValue as { min: number }).min > 0
      ? moment((filter.searchValue as { min: number }).min).format('YYYY-MM-DD')
      : ''
  )
  const [dateRangeMax, setDateRangeMax] = useState(() =>
    isRange && selectedType?.type === 'dateRange' && (filter.searchValue as { max: number }).max < moment('2050-01-01').valueOf()
      ? moment((filter.searchValue as { max: number }).max).format('YYYY-MM-DD')
      : ''
  )
  const [textValue, setTextValue] = useState(() => (typeof filter.searchValue === 'string' ? filter.searchValue : ''))
  const [minMax, setMinMax] = useState('between')

  const commitRange = (min: number, max: number) => {
    onChange({ ...filter, searchValue: { min, max } })
  }

  const commitDateRange = (min: string, max: string) => {
    onChange({
      ...filter,
      searchValue: {
        min: min ? moment(min).startOf('day').valueOf() : 0,
        max: max ? moment(max).endOf('day').valueOf() : moment('2050-01-01').valueOf()
      }
    })
  }

  return (
    <Box display='flex' alignItems='center' style={{ gap: 7, marginBottom: 4 }}>
      <Select
        variant='outlined'
        value={filter.searchType}
        style={{ height: 40, minWidth: 180 }}
        onChange={e => {
          const newType = e.target.value as FilterKey
          setRangeMin(0)
          setRangeMax(0)
          setDateRangeMin('')
          setDateRangeMax('')
          setTextValue('')
          onChange({ ...filter, searchType: newType, searchValue: null })
        }}>
        {filterableSearchTypes.filter(t => t.key === filter.searchType || !usedKeys.has(t.key)).map(type => (
          <MenuItem key={type.key} value={type.key}>
            {type.title}
          </MenuItem>
        ))}
      </Select>

      {selectedType?.type === 'select' ? (
        <Select
          variant='outlined'
          value={filter.searchValue || ''}
          style={{ height: 40, minWidth: 150 }}
          onChange={e => onChange({ ...filter, searchValue: e.target.value as string })}>
          {(selectedType.options || []).map(opt => (
            <MenuItem key={opt.key} value={opt.key}>
              {opt.value}
            </MenuItem>
          ))}
        </Select>
      ) : selectedType?.type === 'boolean' ? (
        <Select
          variant='outlined'
          value={filter.searchValue === true ? 'yes' : filter.searchValue === false ? 'no' : ''}
          style={{ height: 40, minWidth: 100 }}
          onChange={e => onChange({ ...filter, searchValue: e.target.value === 'yes' })}>
          <MenuItem value='yes'>Yes</MenuItem>
          <MenuItem value='no'>No</MenuItem>
        </Select>
      ) : selectedType?.type === 'range' ? (
        <>
          <Select
            variant='outlined'
            value={minMax}
            style={{ height: 40 }}
            onChange={e => {
              setMinMax(e.target.value as string)
              setRangeMin(0)
              setRangeMax(0)
            }}>
            <MenuItem value='min'>Greater Than</MenuItem>
            <MenuItem value='max'>Less Than</MenuItem>
            <MenuItem value='between'>Between</MenuItem>
          </Select>
          {(minMax === 'min' || minMax === 'between') && (
            <TextField
              variant='standard'
              type='number'
              label='Min'
              size='small'
              style={{ width: 80 }}
              value={rangeMin}
              onChange={e => setRangeMin(+e.target.value)}
            />
          )}
          {(minMax === 'max' || minMax === 'between') && (
            <TextField
              variant='standard'
              type='number'
              label='Max'
              size='small'
              style={{ width: 80 }}
              value={rangeMax}
              onChange={e => setRangeMax(+e.target.value)}
            />
          )}
          <IconButton
            size='small'
            onClick={() => {
              const min = minMax === 'max' ? 0 : rangeMin
              const max = minMax === 'min' ? 2147483647 : rangeMax
              commitRange(min, max)
            }}>
            <Search />
          </IconButton>
        </>
      ) : selectedType?.type === 'dateRange' ? (
        <>
          <Select
            variant='outlined'
            value={minMax}
            style={{ height: 40 }}
            onChange={e => {
              setMinMax(e.target.value as string)
              setDateRangeMin('')
              setDateRangeMax('')
            }}>
            <MenuItem value='min'>After</MenuItem>
            <MenuItem value='max'>Before</MenuItem>
            <MenuItem value='between'>Between</MenuItem>
          </Select>
          {(minMax === 'min' || minMax === 'between') && (
            <TextField variant='standard' type='date' size='small' style={{ width: 140 }} value={dateRangeMin} onChange={e => setDateRangeMin(e.target.value)} />
          )}
          {(minMax === 'max' || minMax === 'between') && (
            <TextField variant='standard' type='date' size='small' style={{ width: 140 }} value={dateRangeMax} onChange={e => setDateRangeMax(e.target.value)} />
          )}
          <IconButton
            size='small'
            onClick={() => {
              const min = minMax === 'max' ? '' : dateRangeMin
              const max = minMax === 'min' ? '' : dateRangeMax
              commitDateRange(min, max)
            }}>
            <Search />
          </IconButton>
        </>
      ) : (
        <>
          <TextField
            variant='outlined'
            size='small'
            placeholder='Search...'
            value={textValue}
            onChange={e => setTextValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') onChange({ ...filter, searchValue: textValue })
            }}
          />
          <IconButton size='small' onClick={() => onChange({ ...filter, searchValue: textValue })}>
            <Search />
          </IconButton>
        </>
      )}

      <IconButton size='small' onClick={onRemove}>
        <Close fontSize='small' />
      </IconButton>

      {filter.searchValue !== null && (
        <Chip
          size='small'
          label={`${selectedType?.title}: ${typeof filter.searchValue === 'object' && filter.searchValue !== null ? `${(filter.searchValue as any).min}–${(filter.searchValue as any).max}` : String(filter.searchValue)}`}
          color='primary'
          variant='outlined'
        />
      )}
    </Box>
  )
}

let nextFilterId = 1

const useHashParams = () => {
  const hash = window.location.hash
    .replace('?', '')
    .replace('#', '')
    .split('&')
    .reduce((prev, current) => {
      const eqIdx = current.indexOf('=')
      const key = eqIdx === -1 ? current : current.slice(0, eqIdx)
      const value = eqIdx === -1 ? '' : current.slice(eqIdx + 1)
      return {
        ...prev,
        [key]: decodeURIComponent(value)
      }
    }, {}) as any

  if (
    typeof hash.searchValue === 'string' &&
    (hash.searchValue.startsWith('{') ||
      hash.searchValue === 'false' ||
      hash.searchValue === 'true' ||
      hash.searchValue === 'null')
  ) {
    try {
      hash.searchValue = JSON.parse(hash.searchValue)
    } catch {
      // leave as string if it's not valid JSON
    }
  }

  return hash
}

export const PairedBoards = () => {
  const { getAccessTokenSilently } = useAuth0()
  const hashParams = useHashParams()
  const [perPage, setPerPage] = useState(hashParams.perPage ? +hashParams.perPage : 50)
  const [sortBy, setSortBy] = useState<BoardDeviceAssociationSortBy>(
    hashParams.sortBy || BoardDeviceAssociationSortBy.boardDeviceAssociationCreated
  )
  const [sortOrder, setSortOrder] = useState<BoardDeviceAssociationSortOrder>(BoardDeviceAssociationSortOrder.desc)
  const [searchValue, setSearchValue] = useState<SearchValueType>(hashParams.searchValue || null)
  const [page, setPage] = useState(hashParams.page ? +hashParams.page : 0)
  const [searchType, setSearchType] = useState(hashParams?.searchType || 'terms')
  const [searchModel, setSearchModel] = useState(hashParams?.searchModel || 'boardDeviceAssociations')
  const [exportDateMin, setExportDateMin] = useState('')
  const [exportDateMax, setExportDateMax] = useState('')
  const [exportDateField, setExportDateField] = useState<
    | 'boardDeviceAssociationCreated'
    | 'plusRenewalDate'
    | 'productApplicationCreated'
    | 'stripeCurrentPeriodEnd'
    | 'stripeCurrentPeriodStart'
    | 'stripeSubscriptionCreated'
    | 'stripeStartDate'
    | 'stripeCanceledAt'
    | 'stripeCancelAt'
    | 'stripeEndedAt'
    | 'stripeTrialStart'
    | 'stripeTrialEnd'
    | 'stripeBillingCycleAnchor'
  >('boardDeviceAssociationCreated')
  const [exportDialogOpen, setExportDialogOpen] = useState(false)
  const [selectedDevices, setSelectedDevices] = useState<Map<string, BoardDeviceAssociation>>(new Map())
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false)
  const [upgrading, setUpgrading] = useState(false)
  const [upgradeResults, setUpgradeResults] = useState<Map<string, string>>(new Map())
  const [forceHawkbit, setForceHawkbit] = useState(false)
  const [upgradeFirmware] = useRequestFirmwareUpgrade()
  const [reindexing, setReindexing] = useState(false)
  const [secondaryFilters, setSecondaryFilters] = useState<SecondaryFilter[]>(() => {
    try {
      const raw = hashParams.filters
      if (!raw) return []
      const parsed = JSON.parse(raw) as Array<{ searchType: FilterKey; searchValue: SearchValueType }>
      return parsed.map(f => ({ id: nextFilterId++, searchType: f.searchType, searchValue: f.searchValue }))
    } catch {
      return []
    }
  })
  const secondaryFilterVars = useMemo(() => {
    const vars: Partial<BoardDeviceAssociationInput> = {}
    secondaryFilters.forEach(f => {
      if (f.searchValue !== null && f.searchType !== searchType) {
        ;(vars as any)[f.searchType] = typeof f.searchValue === 'string' ? f.searchValue.trim() : f.searchValue
      }
    })
    return vars
  }, [secondaryFilters, searchType])

  const variables: BoardDeviceAssociationInput = useMemo(
    () =>
      searchType === 'isPlus'
        ? {
            isPlus: true,
            ...secondaryFilterVars,
            sortBy: (sortBy as unknown) as BoardDeviceAssociationInput['sortBy'],
            sortOrder: (sortOrder as unknown) as BoardDeviceAssociationInput['sortOrder']
          }
        : searchType === 'mysteryBoards'
        ? {
            mysteryBoards: true,
            ...secondaryFilterVars,
            sortBy: (sortBy as unknown) as BoardDeviceAssociationInput['sortBy'],
            sortOrder: (sortOrder as unknown) as BoardDeviceAssociationInput['sortOrder']
          }
        : searchType === 'hasStripeSubscription'
        ? {
            hasStripeSubscription: true,
            ...secondaryFilterVars,
            sortBy: (sortBy as unknown) as BoardDeviceAssociationInput['sortBy'],
            sortOrder: (sortOrder as unknown) as BoardDeviceAssociationInput['sortOrder']
          }
        : {
            ...(searchValue || searchValue === false
              ? {
                  [searchType]: typeof searchValue === 'string' ? (searchValue || '').trim() : searchValue
                }
              : {}),
            ...secondaryFilterVars,
            sortBy: (sortBy as unknown) as BoardDeviceAssociationInput['sortBy'],
            sortOrder: (sortOrder as unknown) as BoardDeviceAssociationInput['sortOrder']
          },
    [searchType, searchValue, sortBy, sortOrder, secondaryFilterVars]
  )
  const { data: viewerData } = useGetViewer()
  const { data, loading, error, refetch } = useBoardDeviceAssociations({
    input: {
      ...variables,
      perPage,
      page
    }
  })
  const { data: displayGroupsData } = useListDisplayGroups({
    input: {
      all: true,
      limit: 1000
    }
  })
  const [reindexBdaByBoardId] = useMutation(REINDEX_MUTATION)

  // Build a map of boardId -> groupInfo for quick lookups
  const boardToGroupMap = useMemo<BoardToGroupMap>(() => {
    const map: BoardToGroupMap = {}
    const groups = displayGroupsData?.listDisplayGroups?.displayGroups || []
    groups.forEach(group => {
      // boards is a 2D array
      group.boards.flat().forEach(board => {
        if (board?.id) {
          map[board.id] = {
            groupId: group.id,
            groupTitle: group.title
          }
        }
      })
    })
    return map
  }, [displayGroupsData])

  const boardDeviceAssociations = data?.listBoardDeviceAssociations.boardDeviceAssociations || []
  const pagination = data?.listBoardDeviceAssociations?.pagination

  useEffect(() => {
    const searchValueText = typeof searchValue === 'string' ? searchValue : JSON.stringify(searchValue)
    const filtersParam = secondaryFilters.length > 0
      ? `&filters=${encodeURIComponent(JSON.stringify(secondaryFilters.map(f => ({ searchType: f.searchType, searchValue: f.searchValue }))))}`
      : ''
    window.location.hash = `?sortBy=${sortBy}&sortOrder=${sortOrder}&page=${page}&perPage=${perPage}&searchType=${searchType}&searchValue=${
      searchValueText || ''
    }&searchModel=${searchModel}${filtersParam}`
  }, [sortBy, sortOrder, page, searchType, searchValue, perPage, searchModel, secondaryFilters])

  const exportVariables = useMemo<BoardDeviceAssociationInput>(() => {
    const vars: Record<string, any> = { ...variables }
    if (exportDateMin || exportDateMax) {
      vars[exportDateField] = {
        min: exportDateMin ? moment(exportDateMin).startOf('day').valueOf() : 0,
        max: exportDateMax ? moment(exportDateMax).endOf('day').valueOf() : moment().endOf('day').valueOf()
      }
    }
    return vars as BoardDeviceAssociationInput
  }, [variables, exportDateMin, exportDateMax, exportDateField])

  const canBatchUnpair = FULL_ACCESS.includes(viewerData?.viewer?.account?.emailAddress || 'NOT_EDITOR')

  const currentPageDeviceIds = boardDeviceAssociations.map(bda => bda.deviceId)
  const allPageSelected =
    currentPageDeviceIds.length > 0 && currentPageDeviceIds.every(id => selectedDevices.has(id))
  const somePageSelected = currentPageDeviceIds.some(id => selectedDevices.has(id))

  const checkboxColumn = {
    title: (
      <Checkbox
        checked={allPageSelected}
        indeterminate={somePageSelected && !allPageSelected}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const next = new Map(selectedDevices)
          if (e.target.checked) {
            boardDeviceAssociations.forEach(bda =>
              next.set(bda.deviceId, bda)
            )
          } else {
            boardDeviceAssociations.forEach(bda => next.delete(bda.deviceId))
          }
          setSelectedDevices(next)
        }}
      />
    ),
    width: 50,
    render: (bda: BoardDeviceAssociation) => (
      <Checkbox
        checked={selectedDevices.has(bda.deviceId)}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const next = new Map(selectedDevices)
          if (e.target.checked) next.set(bda.deviceId, bda)
          else next.delete(bda.deviceId)
          setSelectedDevices(next)
        }}
      />
    )
  }

  const allColumns = [
    checkboxColumn,
    ...columns(boardToGroupMap, async boardId => {
      await reindexBdaByBoardId({ variables: { boardId } })
      await refetch()
    })
  ]

  const upgradeViaWebsocket = async (device: BoardDeviceAssociation): Promise<{ method: string; status: string; csvStatus: string }> => {
    try {
      const result = await upgradeFirmware({
        variables: { input: { device: device.deviceId, osVersionId: DEFAULT_OS_VERSION_ID, notes: 'Bulk upgrade via superadmin' } }
      })
      if (result.data?.upgradeFirmware?.error) {
        return { method: 'websocket', status: `error: ${result.data.upgradeFirmware.error}`, csvStatus: `no - ${result.data.upgradeFirmware.error}` }
      }
      return { method: 'websocket', status: 'sent via WebSocket', csvStatus: 'yes - websocket' }
    } catch (e: any) {
      return { method: 'websocket', status: `error: ${e.message || 'unknown'}`, csvStatus: `no - ${e.message || 'error'}` }
    }
  }

  const upgradeViaHawkbit = async (device: BoardDeviceAssociation, token: string): Promise<{ method: string; status: string; csvStatus: string }> => {
    for (let attempt = 0; attempt < 3; attempt++) {
      await sleep(5000)
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 30000)
      try {
        const response = await fetch(`https://platform.vestaboard.com/v3.0/force-upgrade/${device.deviceId}`, {
          method: 'POST',
          headers: { 'X-Vestaboard-Pairing-Code': device.pairingCode!, 'X-Vestaboard-Token': token },
          signal: controller.signal
        })
        if (response.ok) {
          return { method: 'hawkbit', status: 'queued via HawkBit', csvStatus: 'yes - hawkbit' }
        }
        if (attempt < 2) {
          setUpgradeResults(prev => new Map(prev).set(device.deviceId, `retrying (${attempt + 1}/3)`))
          continue
        }
        return { method: 'hawkbit', status: `error-${response.status}`, csvStatus: `no - error ${response.status}` }
      } catch (e) {
        if (attempt < 2) {
          setUpgradeResults(prev => new Map(prev).set(device.deviceId, `retrying (${attempt + 1}/3)`))
          continue
        }
        const reason = e instanceof DOMException && e.name === 'AbortError' ? 'timeout' : 'error'
        return { method: 'hawkbit', status: reason, csvStatus: `no - ${reason}` }
      } finally {
        clearTimeout(timeoutId)
      }
    }
    return { method: 'hawkbit', status: 'error', csvStatus: 'no - exhausted retries' }
  }

  const handleBulkUpgrade = async () => {
    setUpgrading(true)
    setUpgradeResults(new Map())
    const csvRows: Array<{ boardId: string; deviceId: string; upgradingFrom: string; method: string; result: string }> = []

    try {
      const token = await getAccessTokenSilently()
      for (const device of Array.from(selectedDevices.values())) {
        const canUseWebsocket = meetsWebsocketMinimum(device.firmwareVersion) && !forceHawkbit

        if (canUseWebsocket) {
          if (!device.online) {
            csvRows.push({ boardId: device.boardId, deviceId: device.deviceId, upgradingFrom: device.firmwareVersion || '', method: 'websocket', result: 'no - offline' })
            setUpgradeResults(prev => new Map(prev).set(device.deviceId, 'skipped - offline'))
            continue
          }
          const result = await upgradeViaWebsocket(device)
          csvRows.push({ boardId: device.boardId, deviceId: device.deviceId, upgradingFrom: device.firmwareVersion || '', method: result.method, result: result.csvStatus })
          setUpgradeResults(prev => new Map(prev).set(device.deviceId, result.status))
        } else {
          if (!device.pairingCode) {
            csvRows.push({ boardId: device.boardId, deviceId: device.deviceId, upgradingFrom: device.firmwareVersion || '', method: 'hawkbit', result: 'no - no pairing code' })
            setUpgradeResults(prev => new Map(prev).set(device.deviceId, 'skipped - no pairing code'))
            continue
          }
          const result = await upgradeViaHawkbit(device, token)
          csvRows.push({ boardId: device.boardId, deviceId: device.deviceId, upgradingFrom: device.firmwareVersion || '', method: result.method, result: result.csvStatus })
          setUpgradeResults(prev => new Map(prev).set(device.deviceId, result.status))
        }
      }

      const csvContent =
        'boardId,deviceId,upgradingFrom,method,result\n' + csvRows.map(r => `${r.boardId},${r.deviceId},${r.upgradingFrom},${r.method},${r.result}`).join('\n')
      const blob = new Blob([csvContent], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `firmware-upgrade-${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } finally {
      setUpgrading(false)
    }
  }

  const handleBulkReindex = async () => {
    setReindexing(true)
    const boardIds = new Set(Array.from(selectedDevices.values()).map(d => d.boardId))
    for (const boardId of Array.from(boardIds)) {
      await reindexBdaByBoardId({ variables: { boardId } })
    }
    await refetch()
    setReindexing(false)
  }

  return (
    <>
      <SuperGrid
        title='Paired Boards'
        searchType={searchType}
        setSort={({ by, order }) => {
          setSortBy(by)
          setSortOrder(order)
        }}
        columns={allColumns}
        rows={boardDeviceAssociations}
        sortBy={sortBy}
        sortOrder={sortOrder}
        pagination={pagination}
        setPage={setPage}
        setPerPage={setPerPage}
        rowsPerPageOptions={[50, 100, 150]}
        error={!!error}
        loading={loading}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        disableWarnings={searchModel !== 'boardDeviceAssociations'}
        renderSearch={
          <Box display='flex' alignItems='center' style={{ gap: 7 }}>
            <AdvancedSearch
              searchTypes={searchTypes}
              value={searchValue}
              setValue={value => {
                setSearchModel('boardDeviceAssociations')
                setPage(0)
                setSearchValue(value)
              }}
              searchType={searchType}
              setSearchType={setSearchType}
            />
            <IconButton
              size='small'
              title='Add filter'
              onClick={() => {
                const usedKeys = new Set([searchType, ...secondaryFilters.map(f => f.searchType)])
                const available = filterableSearchTypes.find(t => !usedKeys.has(t.key))
                if (!available) return
                setSecondaryFilters(prev => [
                  ...prev,
                  { id: nextFilterId++, searchType: available.key, searchValue: null }
                ])
              }}>
              <Add />
            </IconButton>
          </Box>
        }
        renderBelowHeader={
          secondaryFilters.length > 0 ? (
            <Box display='flex' flexDirection='column' alignItems='flex-end' style={{ marginTop: 8 }}>
              {secondaryFilters.map(filter => {
                const usedKeys = new Set([
                  searchType,
                  ...secondaryFilters.filter(f => f.id !== filter.id).map(f => f.searchType)
                ])
                return (
                  <SecondaryFilterRow
                    key={filter.id}
                    filter={filter}
                    usedKeys={usedKeys}
                    onChange={updated => {
                      setSecondaryFilters(prev => prev.map(f => (f.id === updated.id ? updated : f)))
                      setPage(0)
                    }}
                    onRemove={() => {
                      setSecondaryFilters(prev => prev.filter(f => f.id !== filter.id))
                      setPage(0)
                    }}
                  />
                )
              })}
            </Box>
          ) : undefined
        }
        centerButtons={
          <Box display='flex'>
            {selectedDevices.size > 1 ? (
              <>
                <Button variant='contained' color='primary' onClick={() => setUpgradeDialogOpen(true)}>
                  Upgrade {selectedDevices.size} Devices
                </Button>
                <Box width={7} />
                <Button variant='outlined' onClick={handleBulkReindex} disabled={reindexing}>
                  {reindexing ? <CircularProgress size={20} /> : `Reindex ${selectedDevices.size} Selected`}
                </Button>
                <Box width={7} />
              </>
            ) : (
              <>
                <CsvImport />
                {canBatchUnpair ? <BatchUnPair /> : null}
                <Box width={7} />
              </>
            )}
            <Button
              variant='outlined'
              onClick={() => {
                if (selectedDevices.size > 0) {
                  const selected = Array.from(selectedDevices.values())
                  const fmt = (v: any) => (v ? moment(v).format(DATE_TIME_FORMAT) : '')
                  const esc = (v: any) => {
                    const s = String(v ?? '')
                    return s.includes(',') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s
                  }
                  const headers = [
                    'boardId', 'boardTitle', 'deviceId', 'deviceCreated', 'boardCreated',
                    'boardDeviceAssociationCreated', 'deviceLastSeen', 'boardDeleted', 'pairingCode',
                    'ssid', 'firmwareVersion', 'osVersionName', 'personId', 'personFirstName',
                    'personLastName', 'accountEmailAddress', 'members', 'online', 'lastMessage',
                    'messagesAllTime', 'messagesInPastThirtyDays', 'messagesInPastSevenDays',
                    'messagesInPastDay', 'channels', 'enabledChannels', 'productApplicationId',
                    'productApplicationCreated', 'plusRenewalDate', 'productPricingRecurringFrequency',
                    'yoctoStatus', 'deviceModelTitle', 'boiSerialNumber', 'stripeSubscriptionId',
                    'stripeStatus', 'stripeCurrentPeriodEnd', 'stripeInterval',
                    'stripeCurrentPeriodStart', 'stripeSubscriptionCreated', 'stripeStartDate',
                    'stripeCanceledAt', 'stripeCancelAt', 'stripeEndedAt', 'stripeTrialStart',
                    'stripeTrialEnd', 'stripeBillingCycleAnchor', 'hubspotDealId',
                    'transitionStrategy', 'internalBoard', 'memberEmailAddresses'
                  ]
                  const rows = selected.map(bda => [
                    bda.boardId, esc(bda.boardTitle), bda.deviceId, fmt(bda.deviceCreated),
                    fmt(bda.boardCreated), fmt(bda.boardDeviceAssociationCreated), fmt(bda.deviceLastSeen),
                    bda.boardDeleted, bda.pairingCode || '', esc(bda.ssid || ''),
                    bda.firmwareVersion || '', bda.osVersionName || '', bda.personId || '',
                    esc(bda.personFirstName || ''), esc(bda.personLastName || ''),
                    bda.accountEmailAddress || '', bda.memberIds.length, bda.online,
                    fmt(bda.lastMessage), bda.messagesAllTime || 0, bda.messagesInPastThirtyDays || 0,
                    bda.messagesInPastSevenDays || 0, bda.messagesInPastDay || 0,
                    bda.channels || 0, bda.enabledChannels || 0, bda.productApplicationId || '',
                    fmt(bda.productApplicationCreated), fmt(bda.plusRenewalDate),
                    bda.productPricingRecurringFrequency || '', bda.yoctoStatus || '',
                    bda.deviceModelTitle || '', bda.boiSerialNumber || '',
                    bda.stripeSubscriptionId || '', bda.stripeStatus || '',
                    fmt(bda.stripeCurrentPeriodEnd), bda.stripeInterval || '',
                    fmt(bda.stripeCurrentPeriodStart), fmt(bda.stripeSubscriptionCreated),
                    fmt(bda.stripeStartDate), fmt(bda.stripeCanceledAt), fmt(bda.stripeCancelAt),
                    fmt(bda.stripeEndedAt), fmt(bda.stripeTrialStart), fmt(bda.stripeTrialEnd),
                    fmt(bda.stripeBillingCycleAnchor), bda.hubspotDealId || '',
                    bda.transitionStrategy || 'classic', bda.internalBoard,
                    esc(bda.memberEmailAddresses.join(' '))
                  ].join(','))
                  const csvContent = headers.join(',') + '\n' + rows.join('\n')
                  const blob = new Blob([csvContent], { type: 'text/csv' })
                  const url = URL.createObjectURL(blob)
                  const a = document.createElement('a')
                  a.href = url
                  a.download = `selected-devices-${new Date().toISOString().split('T')[0]}.csv`
                  document.body.appendChild(a)
                  a.click()
                  document.body.removeChild(a)
                  URL.revokeObjectURL(url)
                } else {
                  setExportDialogOpen(true)
                }
              }}>
              {selectedDevices.size > 0 ? `Export ${selectedDevices.size} Selected` : 'Export CSV'}
            </Button>
            <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)}>
              <DialogTitle>Export Paired Boards</DialogTitle>
              <DialogContent>
                <Box display='flex' flexDirection='column' style={{ gap: 16, minWidth: 300 }}>
                  <TextField
                    variant='standard'
                    select
                    label='Filter Date Field'
                    value={exportDateField}
                    onChange={e =>
                      setExportDateField(e.target.value as typeof exportDateField)
                    }
                    SelectProps={{ native: true }}>
                    <option value='boardDeviceAssociationCreated'>Pairing Date</option>
                    <option value='plusRenewalDate'>Plus Renewal Date</option>
                    <option value='productApplicationCreated'>Plus Subscription Created Date</option>
                    <option value='stripeCurrentPeriodEnd'>Stripe Period End</option>
                    <option value='stripeCurrentPeriodStart'>Stripe Period Start</option>
                    <option value='stripeSubscriptionCreated'>Stripe Sub Created</option>
                    <option value='stripeStartDate'>Stripe Start Date</option>
                    <option value='stripeCanceledAt'>Stripe Canceled At</option>
                    <option value='stripeCancelAt'>Stripe Cancel At</option>
                    <option value='stripeEndedAt'>Stripe Ended At</option>
                    <option value='stripeTrialStart'>Stripe Trial Start</option>
                    <option value='stripeTrialEnd'>Stripe Trial End</option>
                    <option value='stripeBillingCycleAnchor'>Stripe Billing Anchor</option>
                  </TextField>
                  <TextField
                    variant='standard'
                    type='date'
                    label={`${exportDateFieldLabels[exportDateField] || 'Date'} From`}
                    value={exportDateMin}
                    onChange={e => setExportDateMin(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    variant='standard'
                    type='date'
                    label={`${exportDateFieldLabels[exportDateField] || 'Date'} To`}
                    value={exportDateMax}
                    onChange={e => setExportDateMax(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                  <Button
                    onClick={() => {
                      setExportDateMin('')
                      setExportDateMax('')
                      setExportDateField('boardDeviceAssociationCreated')
                    }}>
                    CLEAR DATES (Full Report)
                  </Button>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
                <CsvExport
                  query={BOARD_DEVICE_ASSOCIATIONS}
                  variables={{
                    input: exportVariables
                  }}
                  getPagination={({ data }) => ({
                    ...data.listBoardDeviceAssociations.pagination,
                    page: data.listBoardDeviceAssociations.pagination.page + 1
                  })}
                  formatData={({ data }) => {
                    const newData = data?.listBoardDeviceAssociations.boardDeviceAssociations.map(
                      (bda: BoardDeviceAssociation) => ({
                        ...bda,
                        boardDeviceAssociationCreated: bda.boardDeviceAssociationCreated
                          ? moment(bda.boardDeviceAssociationCreated).format(DATE_TIME_FORMAT)
                          : null,
                        deviceCreated: bda.deviceCreated ? moment(bda.deviceCreated).format(DATE_TIME_FORMAT) : null,
                        boardCreated: bda.boardCreated ? moment(bda.boardCreated).format(DATE_TIME_FORMAT) : null,
                        productApplicationCreated: bda.productApplicationCreated
                          ? moment(bda.productApplicationCreated).format(DATE_TIME_FORMAT)
                          : null,
                        plusRenewalDate: bda.plusRenewalDate
                          ? moment(bda.plusRenewalDate).format(DATE_TIME_FORMAT)
                          : null,
                        lastMessage: bda.lastMessage ? moment(bda.lastMessage).format(DATE_TIME_FORMAT) : null,
                        deviceLastSeen: bda.deviceLastSeen ? moment(bda.deviceLastSeen).format(DATE_TIME_FORMAT) : null,
                        lastIndexed: bda.lastIndexed ? moment(bda.lastIndexed).format(DATE_TIME_FORMAT) : null,
                        memberEmailAddresses: bda.memberEmailAddresses.join(' '),
                        members: `${bda.memberIds.length}` || '0',
                        osVersionName: bda?.osVersionName,
                        transitionStrategy: bda?.transitionStrategy,
                        transitionStepSize: bda?.transitionStepSize,
                        stripeSubscriptionId: bda?.stripeSubscriptionId || '',
                        stripeStatus: bda?.stripeStatus || '',
                        stripeCurrentPeriodEnd: bda.stripeCurrentPeriodEnd
                          ? moment(bda.stripeCurrentPeriodEnd).format(DATE_TIME_FORMAT)
                          : null,
                        stripeCurrentPeriodStart: bda.stripeCurrentPeriodStart
                          ? moment(bda.stripeCurrentPeriodStart).format(DATE_TIME_FORMAT)
                          : null,
                        stripeInterval: bda?.stripeInterval || '',
                        stripeSubscriptionCreated: bda.stripeSubscriptionCreated
                          ? moment(bda.stripeSubscriptionCreated).format(DATE_TIME_FORMAT)
                          : null,
                        stripeStartDate: bda.stripeStartDate
                          ? moment(bda.stripeStartDate).format(DATE_TIME_FORMAT)
                          : null,
                        stripeCanceledAt: bda.stripeCanceledAt
                          ? moment(bda.stripeCanceledAt).format(DATE_TIME_FORMAT)
                          : null,
                        stripeCancelAt: bda.stripeCancelAt
                          ? moment(bda.stripeCancelAt).format(DATE_TIME_FORMAT)
                          : null,
                        stripeEndedAt: bda.stripeEndedAt
                          ? moment(bda.stripeEndedAt).format(DATE_TIME_FORMAT)
                          : null,
                        stripeTrialStart: bda.stripeTrialStart
                          ? moment(bda.stripeTrialStart).format(DATE_TIME_FORMAT)
                          : null,
                        stripeTrialEnd: bda.stripeTrialEnd
                          ? moment(bda.stripeTrialEnd).format(DATE_TIME_FORMAT)
                          : null,
                        stripeBillingCycleAnchor: bda.stripeBillingCycleAnchor
                          ? moment(bda.stripeBillingCycleAnchor).format(DATE_TIME_FORMAT)
                          : null
                      })
                    )

                    // boards and devices must be 1:1 — flag mismatches
                    const devicesPerBoard: Record<string, Set<string>> = {}
                    const boardsPerDevice: Record<string, Set<string>> = {}
                    for (const bda of newData) {
                      ;(devicesPerBoard[bda.boardId] || (devicesPerBoard[bda.boardId] = new Set())).add(bda.deviceId)
                      ;(boardsPerDevice[bda.deviceId] || (boardsPerDevice[bda.deviceId] = new Set())).add(bda.boardId)
                    }

                    return newData.map((bda: { boardId: string; deviceId: string }) => ({
                      ...bda,
                      warningBoardIdDuplicate: boardsPerDevice[bda.deviceId].size > 1,
                      warningDeviceIdDuplicate: devicesPerBoard[bda.boardId].size > 1
                    }))
                  }}
                />
              </DialogActions>
            </Dialog>
            <Dialog
              open={upgradeDialogOpen}
              onClose={() => !upgrading && setUpgradeDialogOpen(false)}
              maxWidth='sm'
              fullWidth>
              <DialogTitle>Confirm Firmware Upgrade</DialogTitle>
              <DialogContent>
                <Typography>
                  {selectedDevices.size} {selectedDevices.size === 1 ? 'device' : 'devices'} will be upgraded.
                </Typography>
                <Typography variant='body2' style={{ marginTop: 4, color: 'rgba(0,0,0,0.54)' }}>
                  {(() => {
                    const devices = Array.from(selectedDevices.values())
                    const wsEligible = devices.filter(d => meetsWebsocketMinimum(d.firmwareVersion) && !forceHawkbit)
                    const wsOnline = wsEligible.filter(d => d.online).length
                    const wsOffline = wsEligible.length - wsOnline
                    const hbCount = devices.length - wsEligible.length
                    const parts: string[] = []
                    if (wsOnline > 0) parts.push(`${wsOnline} via WebSocket`)
                    if (wsOffline > 0) parts.push(`${wsOffline} offline (will skip)`)
                    if (hbCount > 0) parts.push(`${hbCount} via HawkBit`)
                    return parts.join(', ')
                  })()}
                </Typography>
                <FormControlLabel
                  style={{ marginTop: 8 }}
                  control={
                    <Switch
                      checked={forceHawkbit}
                      onChange={e => setForceHawkbit(e.target.checked)}
                      disabled={upgrading}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant='body2'>Use HawkBit for all devices</Typography>
                      <Typography variant='caption' style={{ color: 'rgba(0,0,0,0.54)' }}>
                        Devices below v3.1.5 always use HawkBit. When off, v3.1.5+ use WebSocket (faster).
                      </Typography>
                    </Box>
                  }
                />
                <Box
                  style={{
                    maxHeight: 300,
                    overflowY: 'auto',
                    marginTop: 16,
                    border: '1px solid rgba(0,0,0,0.12)',
                    borderRadius: 4,
                    padding: 8
                  }}>
                  {Array.from(selectedDevices.values()).map(device => {
                    const result = upgradeResults.get(device.deviceId)
                    const willUseWs = meetsWebsocketMinimum(device.firmwareVersion) && !forceHawkbit
                    return (
                      <Box
                        key={device.deviceId}
                        style={{
                          padding: '4px 0',
                          fontFamily: 'monospace',
                          fontSize: 13,
                          display: 'flex',
                          alignItems: 'center'
                        }}>
                        {result ? (
                          <Chip
                            size='small'
                            label={result}
                            color={result.startsWith('sent') || result.startsWith('queued') ? 'primary' : result.startsWith('skipped') ? 'default' : 'secondary'}
                            style={{ marginRight: 8 }}
                          />
                        ) : (
                          <Chip
                            size='small'
                            label={willUseWs ? 'WS' : 'HB'}
                            variant='outlined'
                            style={{ marginRight: 8, minWidth: 32 }}
                          />
                        )}
                        {device.deviceId}
                        <Typography variant='caption' style={{ marginLeft: 8, color: 'rgba(0,0,0,0.4)' }}>
                          {device.firmwareVersion || 'unknown'}
                        </Typography>
                        {willUseWs && !device.online && !result && (
                          <Chip size='small' label='offline' style={{ marginLeft: 8 }} />
                        )}
                        {!willUseWs && !device.pairingCode && !result && (
                          <Chip size='small' label='no pairing code' style={{ marginLeft: 8 }} />
                        )}
                      </Box>
                    )
                  })}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => {
                    setUpgradeDialogOpen(false)
                    if (upgradeResults.size > 0) {
                      setSelectedDevices(new Map())
                      setUpgradeResults(new Map())
                    }
                  }}
                  disabled={upgrading}>
                  {upgradeResults.size > 0 && !upgrading ? 'Done' : 'Cancel'}
                </Button>
                {(upgradeResults.size === 0 || upgrading) && (
                  <Button
                    onClick={handleBulkUpgrade}
                    color='primary'
                    variant='contained'
                    disabled={upgrading}>
                    {upgrading ? (
                      <CircularProgress size={20} />
                    ) : (
                      `Upgrade ${selectedDevices.size} Devices`
                    )}
                  </Button>
                )}
              </DialogActions>
            </Dialog>
          </Box>
        }
      />
      {!loading &&
      !boardDeviceAssociations.length &&
      searchModel === 'boardDeviceAssociations' &&
      typeof searchValue === 'string' &&
      isUUID(searchValue) ? (
        <>
          <Box
            style={{
              marginBottom: 10
            }}>
            <Button
              color='primary'
              variant='outlined'
              onClick={async () => {
                await reindexBdaByBoardId({
                  variables: { boardId: searchValue }
                })
                await refetch()
              }}>
              Refresh index for Board Id: {searchValue}
            </Button>
          </Box>
          <Box>
            <Button
              color='primary'
              variant='outlined'
              onClick={() => {
                setSearchModel('deviceId')
              }}>
              Search Unpaired Devices
            </Button>
          </Box>
        </>
      ) : null}

      {typeof searchValue === 'string' && searchModel === 'deviceId' ? <SearchDeviceId id={searchValue} /> : null}
      {!data?.listBoardDeviceAssociations?.boardDeviceAssociations?.length &&
      typeof searchValue === 'string' &&
      searchValue.includes('Vestaboard-') ? (
        <SearchSSIDComponent ssid={searchValue} />
      ) : null}
      {!data?.listBoardDeviceAssociations?.boardDeviceAssociations?.length &&
      typeof searchValue === 'string' &&
      isBoiSerialNumber(searchValue) ? (
        <SearchBoiSerialComponent boiSerialNumber={searchValue} />
      ) : null}
      {!data?.listBoardDeviceAssociations?.boardDeviceAssociations?.length &&
      typeof searchValue === 'string' &&
      searchValue.length >= 6 &&
      searchValue.length <= 7 ? (
        <SearchPairingCodeComponent pairingCode={searchValue} />
      ) : null}
    </>
  )
}
